import LanguagePill from "@/components/elements/LanguagePill";
import {
  EditRounded,
  InfoRounded,
  SubtitlesRounded
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Icon,
  Paper,
  Stack,
  Typography,
  chipClasses
} from "@mui/material";
import React, { useMemo } from "react";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import DubbingIcon from "@/components/assets/DubbingIcon";
import LipSyncIcon from "@/components/assets/LipSyncIcon";
import { setCurrentStep } from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import { CREATE_PROJECT_STEPS } from "@/features/createProject/constants";
import { getFileSizeInText } from "@/utils/misc";
import useProjectCredits from "@/features/credits/hooks/useProjectCredits";
import QualityCheckSmall from "@/components/assets/QualityCheckSmall";
import CreditsIcon from "@/components/assets/CreditsIcon";

interface TableRowProps {
  icon: React.ReactNode;
  title: string;
  caption: string;
  calculationTitle: string;
  calculationCaption: string;
  rate: number;
}

function createData(
  icon: React.ReactNode,
  title: string,
  caption: string,
  calculationTitle: string,
  calculationCaption: string,
  rate: number
) {
  return {
    icon,
    title,
    caption,
    calculationTitle,
    calculationCaption,
    rate
  } as TableRowProps;
}

export const ProjectSummary = (): React.ReactNode => {
  const dispatch = useAppDispatch();
  const isLipSyncSelected = useTypedSelector(
    (state) => state.clientProjectPopup.lipSyncEnabled
  );

  const isQualityCheckSelected = useTypedSelector(
    (state) => state.clientProjectPopup.qualityCheckEnabled
  );

  const { targetLocales, projectId, projectName, sourceFileResponseList } =
    useTypedSelector((state) => state.clientProjectPopup);
  const uppyFiles = useTypedSelector((state) => state.uppyHandler);

  const allFileData = useMemo(() => {
    let size = 0;
    let allFiles = {};
    if (projectId && uppyFiles?.[projectId]) {
      allFiles = Object.assign({}, allFiles, uppyFiles[projectId]);
    }
    if (sourceFileResponseList) {
      allFiles = {
        ...allFiles,
        ...sourceFileResponseList
      };
    }

    Object.values(allFiles).forEach((file: any) => {
      if (file.fileSize) {
        size += Number(file.fileSize);
      }
    });

    return {
      size: getFileSizeInText(size),
      count: Object.keys(allFiles).length || 0
    };
  }, [sourceFileResponseList, uppyFiles, projectId]);

  const rows = useMemo(() => {
    return [
      createData(
        <DubbingIcon />,
        "Dubbing",
        "You’ll get dubbed video in choice of your languages",
        `${(allFileData.count || 0) * (targetLocales?.length || 0)} Dubs`,
        `(${allFileData.count} files x ${targetLocales?.length} languages)`,
        0
      ),
      createData(
        <SubtitlesRounded style={{ height: 20, width: 20 }} />,
        "Dubbed Subtitles (free)",
        "",
        "",
        "",
        0
      ),
      isLipSyncSelected &&
        createData(
          <LipSyncIcon />,
          "Lip sync",
          "You’ll get dubbed video in choice of your languages",
          `${(allFileData.count || 0) * (targetLocales?.length || 0)} Dubs`,
          "",
          0
        ),
      isQualityCheckSelected &&
        createData(
          <QualityCheckSmall style={{ height: 20, width: 20 }} />,
          `Quality Assurance`,
          "Quality assured by humans to ensure precise output",
          `${(allFileData.count || 0) * (targetLocales?.length || 0)} Dubs`,
          "",
          0
        )
    ].filter(Boolean);
  }, [
    isLipSyncSelected,
    isQualityCheckSelected,
    allFileData?.count,
    targetLocales
  ]);

  const projectCredits = useProjectCredits({
    projectId: projectId as string,
    isCreatingProject: true
  });

  const handleEditCTA = (step: number) => {
    dispatch(setCurrentStep(step));
  };

  return (
    <Grid container>
      <Grid item container xs={6} gap={4}>
        <Grid item xs={12}>
          <Stack direction={"column"}>
            <Stack direction={"row"} gap={2} alignItems={"center"}>
              <Typography variant="caption" color={"text.secondary"}>
                Project Name
              </Typography>
              <Box>
                <Button
                  startIcon={<EditRounded />}
                  variant="text"
                  size="small"
                  color="secondary"
                  id="edit-project-name"
                  onClick={() => {
                    handleEditCTA(CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int);
                  }}
                >
                  Edit
                </Button>
              </Box>
            </Stack>
            <Typography
              variant="subtitle1"
              color={"text.primary"}
              sx={{
                maxWidth: 400,
                textOverflow: "ellipsis",
                overflowWrap: "anywhere",
                mt: "2px"
              }}
            >
              {projectName}
            </Typography>
          </Stack>
        </Grid>

        {/* <Grid item xs={12}>
          <Stack direction={"row"} gap={2} alignItems={"center"}>
            <Typography variant="caption" color={"text.secondary"}>
              Source Language
            </Typography>
            <Button
              startIcon={<EditRounded />}
              variant="text"
              size="small"
              color="secondary"
              onClick={() => {
                handleEditCTA(CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int);
              }}
              id="edit-project-languages"
            >
              Edit
            </Button>
          </Stack>
          <Stack direction={"row"} gap={1} mt={1} flexWrap={"wrap"}>
            {!sourceLocale || sourceLocale === LANGUAGES.AUTO ? (
              <Typography variant="caption">Auto-detect</Typography>
            ) : (
              <LanguagePill variant="filled" language={sourceLocale} />
            )}
          </Stack>
        </Grid> */}
        <Grid item xs={12}>
          <Stack direction={"row"} gap={2} alignItems={"center"}>
            <Typography variant="caption" color={"text.secondary"}>
              Dubbing languages ({targetLocales?.length})
            </Typography>
            <Button
              startIcon={<EditRounded />}
              variant="text"
              size="small"
              color="secondary"
              onClick={() => {
                handleEditCTA(CREATE_PROJECT_STEPS.SELECT_LANGUAGE.int);
              }}
              id="edit-project-languages"
            >
              Edit
            </Button>
          </Stack>
          <Stack direction={"row"} gap={1} mt={1} flexWrap={"wrap"}>
            {targetLocales?.map((_language) => (
              <LanguagePill
                variant="filled"
                size="small"
                language={_language}
              />
            ))}
          </Stack>
        </Grid>
      </Grid>
      <Grid item xs={6}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            p: 3,
            border: 0,
            gap: 2,
            display: "grid"
          }}
        >
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Stack direction={"row"} alignItems={"center"}>
              <Typography variant="caption" color={"text.secondary"}>
                Services selected
              </Typography>
              <Button
                startIcon={<EditRounded />}
                variant="text"
                size="small"
                color="secondary"
                onClick={() => {
                  handleEditCTA(CREATE_PROJECT_STEPS.REQUIREMENTS.int);
                }}
                sx={{
                  ml: 1
                }}
                id="edit-project-services"
              >
                Edit
              </Button>
            </Stack>
          </Stack>
          <Grid item xs={12} container gap={2}>
            {rows.map((row) =>
              row ? (
                <Chip
                  size="medium"
                  icon={
                    <Icon
                      sx={{
                        height: 20,
                        width: 20,
                        color: "text.secondary",
                        display: "flex"
                      }}
                    >
                      {row.icon}
                    </Icon>
                  }
                  sx={{
                    [`& .${chipClasses.icon}`]: {
                      mr: -0.25
                    }
                  }}
                  label={row.title}
                />
              ) : null
            )}
          </Grid>
          <Divider orientation="horizontal" sx={{ width: "100%" }}></Divider>
          <Stack
            direction={"row"}
            gap={3}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack>
              <Typography variant="body2" color={"text.primary"}>
                Credit consumption per file
              </Typography>
              <Typography variant="caption" color={"text.secondary"}>
                Final credit usage will be determined by file duration
              </Typography>
            </Stack>
            <Stack
              direction={"row"}
              alignItems={"center"}
              gap={0.5}
              sx={{ flexShrink: 0 }}
            >
              <CreditsIcon
                style={{
                  height: 20,
                  width: 20
                }}
              />
              <Typography variant="subtitle2">
                {projectCredits.creditsRate} credits per min
              </Typography>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
      {allFileData.count ? null : (
        <Grid item xs={12} mt={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              display: "flex",
              gap: 1,
              alignItems: "center",
              borderRadius: 2
            }}
          >
            <InfoRounded
              sx={{
                height: 16,
                width: 16,
                color: "text.secondary"
              }}
            />
            <Typography variant="caption" color={"text.secondary"}>
              You haven’t uploaded any files, an empty project will be created.
              You can still upload files after the project is created
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};
